import React, { useEffect, useMemo, useRef, useState } from 'react';
import { resolveImageUrl } from '../lib/api';
import { FP_VIEWBOX, FP_BLOCKS, FP_DECOR, blockTitle } from '../data/floorplanMap';

const img = (u) => (u ? resolveImageUrl(u) : '');
const igUrl = (v) => `https://instagram.com/${String(v || '').trim().replace(/^@/, '').replace(/^https?:\/\/(www\.)?instagram\.com\//, '').replace(/\/.*$/, '')}`;
const igTxt = (v) => { const s = String(v || '').trim().replace(/^https?:\/\/(www\.)?instagram\.com\//, '').replace(/\/.*$/, ''); return s.startsWith('@') ? s : `@${s}`; };
const ZOOMS = [1, 1.5, 2, 3, 4];

const MAP_CSS = `
.tvpm-box { position: relative; border: 2px solid #111; box-shadow: 6px 6px 0 #111; background: #fff; }
.tvpm-scroll { overflow: auto; max-height: 68vh; -webkit-overflow-scrolling: touch; }
.tvpm-zoom { position: absolute; right: 8px; top: 8px; display: flex; flex-direction: column; gap: 6px; z-index: 2; }
.tvpm-zoom button { width: 30px; height: 30px; background: rgba(255,255,255,.92); border: 2px solid #111; box-shadow: 2px 2px 0 #111; font-weight: 800; font-size: 16px; padding: 0; line-height: 1; cursor: pointer; }
.tvpm-zoom button:disabled { opacity: .35; }
.tvpm-chips { display: flex; gap: 6px; flex-wrap: wrap; margin: 10px 0 12px; }
.tvpm-chip { border: 1.5px solid #111; background: #fff; padding: 6px 10px; font-size: 12px; font-weight: 800; cursor: pointer; }
.tvpm-chip.on { background: #111; color: #CAFF00; }
.tvpm-leg { display: flex; flex-wrap: wrap; gap: 12px; font-size: 11px; font-weight: 700; margin-top: 12px; }
.tvpm-leg i { display: inline-block; width: 12px; height: 12px; border: 1.5px solid #111; vertical-align: -2px; margin-right: 5px; }
.tvpm-sheet { position: fixed; left: 0; right: 0; bottom: 64px; z-index: 30; padding: 0 12px 12px; pointer-events: none; }
.tvpm-sheet > div { pointer-events: auto; max-width: 536px; margin: 0 auto; animation: tvpmUp .22s ease-out; }
@keyframes tvpmUp { from { transform: translateY(24px); opacity: 0; } to { transform: none; opacity: 1; } }
.tvpm-res { display: grid; gap: 6px; margin: -4px 0 12px; }
.tvpm-res button { text-align: left; background: #fff; border: 1.5px solid #111; padding: 8px 10px; font-size: 13px; cursor: pointer; display: flex; justify-content: space-between; gap: 8px; }
`;

function wrapName(name, w, h) {
  for (let fs = 6.5; fs >= 3.4; fs -= 0.4) {
    const cpl = Math.max(3, Math.floor((w - 3) / (fs * 0.58)));
    const lines = [];
    let cur = '';
    String(name || '').split(/\s+/).filter(Boolean).forEach((word) => {
      let wd = word;
      while (wd.length > cpl) { if (cur) { lines.push(cur); cur = ''; } lines.push(wd.slice(0, cpl)); wd = wd.slice(cpl); }
      if (!wd) return;
      if (!cur) cur = wd; else if ((cur + ' ' + wd).length <= cpl) cur += ' ' + wd; else { lines.push(cur); cur = wd; }
    });
    if (cur) lines.push(cur);
    if (lines.length <= 3 && (lines.length + 1.2) * fs * 1.12 <= h - 2) return { fs, lines };
  }
  const fs = 3.4;
  const cpl = Math.max(3, Math.floor((w - 3) / (fs * 0.58)));
  const n = String(name || '');
  return { fs, lines: n.length > cpl ? [n.slice(0, cpl - 1) + '…'] : [n] };
}

function Decor() {
  return FP_DECOR.map((d, i) => {
    if (d[0] === 'r') return <rect key={i} x={d[1]} y={d[2]} width={d[3]} height={d[4]} fill={d[5]} stroke={d[6] || undefined} strokeWidth={d[7] || undefined} rx={d[8] || 0} strokeDasharray={d[9] || undefined} />;
    if (d[0] === 'p') return <path key={i} d={d[1]} fill={d[2]} stroke={d[3] || undefined} strokeWidth={d[4] || undefined} strokeDasharray={d[5] || undefined} />;
    return (
      <text key={i} x={d[1]} y={d[2]} fontSize={d[4]} fill={d[5]} fontWeight={d[6]} textAnchor="middle" dominantBaseline="middle"
        letterSpacing={d[8] || undefined} transform={d[7] ? `rotate(${d[7]} ${d[1]} ${d[2]})` : undefined} fontFamily="Arial, Helvetica, sans-serif">{d[3]}</text>
    );
  });
}

const arrowHead = (x1, y1, x2, y2, w) => {
  const a = Math.atan2(y2 - y1, x2 - x1);
  const L = 7 + w * 2.2;
  const W = 4 + w * 1.4;
  const bx = x2 - L * Math.cos(a);
  const by = y2 - L * Math.sin(a);
  return `${x2},${y2} ${bx - Math.sin(a) * W},${by + Math.cos(a) * W} ${bx + Math.sin(a) * W},${by - Math.cos(a) * W}`;
};

export default function PassportMap({ map, tenants, stampKeys, focusTid, onFocused, onScan }) {
  const [zoom, setZoom] = useState(1);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(null);
  const scrollRef = useRef(null);

  const tById = useMemo(() => { const m = {}; (tenants || []).forEach((t) => { m[t.id] = t; }); return m; }, [tenants]);
  const blocks = useMemo(() => {
    const l = (map && map.layout) || {};
    const hid = new Set(l.hidden || []);
    const ov = l.blocks || {};
    return [...FP_BLOCKS, ...(l.added || [])].filter((b) => !hid.has(b.id)).map((b) => ({ ...b, ...(ov[b.id] || {}) }));
  }, [map]);
  const booths = (map && map.booths) || {};
  const tBlocks = useMemo(() => {
    const m = {};
    Object.entries(booths).forEach(([bid, tid]) => { if (tById[tid]) (m[tid] = m[tid] || []).push(bid); });
    return m;
  }, [booths, tById]);
  const tidList = Object.keys(tBlocks);
  const doneCount = tidList.filter((tid) => stampKeys.has(`t:${tid}`)).length;

  const s = q.trim().toLowerCase();
  const matches = useMemo(() => (!s ? [] : tidList.map((tid) => tById[tid]).filter((t) => [t.name, t.booth, t.category, t.instagram].some((v) => String(v || '').toLowerCase().includes(s)))), [s, tidList.join(','), tById]); // eslint-disable-line react-hooks/exhaustive-deps
  const matchSet = useMemo(() => new Set(matches.map((t) => t.id)), [matches]);
  const visible = (tid) => {
    if (s && !matchSet.has(tid)) return false;
    const got = stampKeys.has(`t:${tid}`);
    if (filter === 'todo') return !got;
    if (filter === 'done') return got;
    return true;
  };

  const centerOn = (tid, z) => {
    const ids = tBlocks[tid] || [];
    const bs = blocks.filter((b) => ids.includes(b.id));
    if (!bs.length) return;
    const cx = bs.reduce((a, b) => a + b.x + b.w / 2, 0) / bs.length;
    const cy = bs.reduce((a, b) => a + b.y + b.h / 2, 0) / bs.length;
    const nz = Math.max(zoom, z || 2);
    setZoom(nz);
    setTimeout(() => {
      const el = scrollRef.current;
      if (!el) return;
      const svg = el.querySelector('svg');
      const W = svg ? svg.getBoundingClientRect().width : el.clientWidth * nz;
      const H = W * (FP_VIEWBOX.h / FP_VIEWBOX.w);
      el.scrollTo({ left: Math.max(0, (cx / FP_VIEWBOX.w) * W - el.clientWidth / 2), top: Math.max(0, (cy / FP_VIEWBOX.h) * H - el.clientHeight / 2), behavior: 'smooth' });
    }, 60);
  };

  const inited = useRef(false);
  useEffect(() => {
    if (inited.current || !map || focusTid) return;
    inited.current = true;
    const el = scrollRef.current;
    if (!el || el.clientWidth >= 700) return;
    const ids = new Set(Object.keys(booths));
    const bs = blocks.filter((b) => ids.has(b.id));
    const src = bs.length ? bs : blocks;
    if (!src.length) return;
    const cx = src.reduce((a, b) => a + b.x + b.w / 2, 0) / src.length;
    const cy = src.reduce((a, b) => a + b.y + b.h / 2, 0) / src.length;
    setZoom(2);
    setTimeout(() => {
      const W = el.clientWidth * 2;
      const H = W * (FP_VIEWBOX.h / FP_VIEWBOX.w);
      el.scrollTo({ left: Math.max(0, (cx / FP_VIEWBOX.w) * W - el.clientWidth / 2), top: Math.max(0, (cy / FP_VIEWBOX.h) * H - el.clientHeight / 2) });
    }, 40);
  }, [map, blocks]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (focusTid && tBlocks[focusTid]) { setSel(focusTid); setFilter('all'); setQ(''); centerOn(focusTid, 2.5); if (onFocused) onFocused(); }
  }, [focusTid, tBlocks]); // eslint-disable-line react-hooks/exhaustive-deps

  const zoomBy = (dir) => {
    const el = scrollRef.current;
    const i = ZOOMS.indexOf(zoom);
    const nz = ZOOMS[Math.min(ZOOMS.length - 1, Math.max(0, (i < 0 ? 0 : i) + dir))];
    if (nz === zoom) return;
    const fx = el ? (el.scrollLeft + el.clientWidth / 2) / (el.scrollWidth || 1) : 0.5;
    const fy = el ? (el.scrollTop + el.clientHeight / 2) / (el.scrollHeight || 1) : 0.5;
    setZoom(nz);
    setTimeout(() => { if (el) el.scrollTo({ left: fx * el.scrollWidth - el.clientWidth / 2, top: fy * el.scrollHeight - el.clientHeight / 2 }); }, 30);
  };

  if (!map) return <div className="tvpp-card" style={{ textAlign: 'center' }}>Memuat peta...</div>;

  const st = sel ? tById[sel] : null;
  const stGot = st && stampKeys.has(`t:${st.id}`);

  return (
    <div data-testid="passport-map">
      <style>{MAP_CSS}</style>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
        <span className="tvpp-h" style={{ fontSize: 20 }}>Peta booth</span>
        <span style={{ fontSize: 12, fontWeight: 700 }} data-testid="map-progress">{doneCount}/{tidList.length} booth sudah kamu stamp</span>
      </div>
      <input className="tvpp-in" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari creator atau nomor booth..." data-testid="map-search" />
      {s && (
        <div className="tvpm-res" style={{ marginTop: 8 }}>
          {matches.slice(0, 6).map((t) => (
            <button type="button" key={t.id} onClick={() => { setSel(t.id); setQ(''); centerOn(t.id, 2.5); }}>
              <b style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</b>
              <span style={{ whiteSpace: 'nowrap', color: '#555' }}>{t.booth || ''}</span>
            </button>
          ))}
          {!matches.length && <div style={{ fontSize: 13, color: '#555' }}>Tidak ada booth yang cocok.</div>}
        </div>
      )}
      <div className="tvpm-chips">
        {[['all', 'Semua booth'], ['todo', 'Belum distamp'], ['done', 'Sudah distamp']].map(([k, l]) => (
          <button type="button" key={k} className={`tvpm-chip ${filter === k ? 'on' : ''}`} onClick={() => setFilter(k)} data-testid={`map-filter-${k}`}>{l}</button>
        ))}
      </div>

      <div className="tvpm-box">
        <div className="tvpm-zoom">
          <button type="button" onClick={() => zoomBy(1)} disabled={zoom >= ZOOMS[ZOOMS.length - 1]} aria-label="Perbesar" data-testid="map-zoom-in">+</button>
          <button type="button" onClick={() => zoomBy(-1)} disabled={zoom <= 1} aria-label="Perkecil" data-testid="map-zoom-out">−</button>
        </div>
        <div className="tvpm-scroll" ref={scrollRef}>
          <svg viewBox={`0 0 ${FP_VIEWBOX.w} ${FP_VIEWBOX.h}`} style={{ width: `${zoom * 100}%`, display: 'block' }} data-testid="map-svg">
            <rect x="0" y="0" width={FP_VIEWBOX.w} height={FP_VIEWBOX.h} fill="#ffffff" />
            <Decor />
            {blocks.map((b) => {
              const tid = booths[b.id];
              const t = tid ? tById[tid] : null;
              if (!t) {
                return (
                  <g key={b.id} opacity={filter !== 'all' || s ? 0.35 : 0.8}>
                    <rect x={b.x} y={b.y} width={b.w} height={b.h} fill="#ECEAE4" stroke="#b9b5ad" strokeWidth={0.8} />
                    <text x={b.x + b.w / 2} y={b.y + b.h / 2} fontSize={7} fontWeight="700" fill="#a39f97" textAnchor="middle" dominantBaseline="middle" fontFamily="Arial, Helvetica, sans-serif">{b.label}</text>
                  </g>
                );
              }
              const got = stampKeys.has(`t:${tid}`);
              const on = visible(tid);
              const isSel = sel === tid;
              const lbl = wrapName(t.name, b.w, b.h);
              const r = Math.min(5.5, b.w / 5);
              return (
                <g key={b.id} onClick={() => setSel(isSel ? null : tid)} style={{ cursor: 'pointer' }} opacity={on ? 1 : 0.22} data-testid={`map-block-${b.id}`}>
                  <title>{`${blockTitle(b)} - ${t.name}`}</title>
                  <rect x={b.x} y={b.y} width={b.w} height={b.h} fill={got ? '#CAFF00' : '#ffffff'} stroke="#111" strokeWidth={1.6} />
                  <text x={b.x + 2} y={b.y + 2 + lbl.fs * 0.85} fontSize={lbl.fs * 0.85} fontWeight="800" fill="#7a3d12" fontFamily="Arial, Helvetica, sans-serif">{b.label}</text>
                  <text x={b.x + b.w / 2} fontSize={lbl.fs} fontWeight="700" fill="#111" textAnchor="middle" fontFamily="Arial, Helvetica, sans-serif">
                    {lbl.lines.map((ln, i) => (
                      <tspan key={i} x={b.x + b.w / 2} y={b.y + b.h / 2 + lbl.fs * 0.8 + (i - (lbl.lines.length - 1) / 2) * lbl.fs * 1.12}>{ln}</tspan>
                    ))}
                  </text>
                  {got && (
                    <g>
                      <circle cx={b.x + b.w - r - 1} cy={b.y + r + 1} r={r} fill="#111" />
                      <path d={`M${b.x + b.w - r * 1.55 - 1},${b.y + r + 1} l${r * 0.4},${r * 0.42} l${r * 0.75},${-r * 0.8}`} fill="none" stroke="#CAFF00" strokeWidth={Math.max(1, r * 0.32)} strokeLinecap="round" strokeLinejoin="round" />
                    </g>
                  )}
                  {isSel && <rect x={b.x - 2} y={b.y - 2} width={b.w + 4} height={b.h + 4} fill="none" stroke="#E63329" strokeWidth={3} pointerEvents="none" />}
                </g>
              );
            })}
            {(((map && map.layout) || {}).lines || []).map((ln) => (
              <g key={ln.id} pointerEvents="none">
                <line x1={ln.x1} y1={ln.y1} x2={ln.x2} y2={ln.y2} stroke={ln.color} strokeWidth={ln.width} strokeLinecap="round" strokeDasharray={ln.dash ? `${ln.width * 3} ${ln.width * 2}` : undefined} />
                {(ln.arrow === 'end' || ln.arrow === 'both') && <polygon points={arrowHead(ln.x1, ln.y1, ln.x2, ln.y2, ln.width)} fill={ln.color} />}
                {ln.arrow === 'both' && <polygon points={arrowHead(ln.x2, ln.y2, ln.x1, ln.y1, ln.width)} fill={ln.color} />}
              </g>
            ))}
          </svg>
        </div>
      </div>
      <div className="tvpm-leg">
        <span><i style={{ background: '#ffffff' }} />Booth creator</span>
        <span><i style={{ background: '#CAFF00' }} />Sudah kamu stamp</span>
        <span><i style={{ background: '#ECEAE4', borderColor: '#b9b5ad' }} />Belum ikut / kosong</span>
      </div>
      <p style={{ fontSize: 12, color: '#555', margin: '10px 0 0', lineHeight: 1.5 }}>Ketuk booth untuk lihat creator. Pakai tombol + / − untuk zoom, geser peta untuk melihat area lain.</p>

      {st && (
        <div className="tvpm-sheet" data-testid="map-sheet">
          <div className="tvpp-card" style={{ padding: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '52px minmax(0,1fr) auto', gap: 12, alignItems: 'center' }}>
              {st.logo ? <img className="tvpp-logo" style={{ width: 52, height: 52 }} src={img(st.logo)} alt="" /> : <div className="tvpp-logo" style={{ width: 52, height: 52, display: 'grid', placeItems: 'center', fontWeight: 800 }}>{String(st.name || '?')[0]}</div>}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 800, fontSize: 16, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{st.name}</div>
                <div style={{ fontSize: 12, color: '#555', marginTop: 2 }}>{[st.booth && `Booth ${st.booth}`, st.category].filter(Boolean).join(' | ') || '-'}</div>
                {st.instagram && <a href={igUrl(st.instagram)} target="_blank" rel="noreferrer" style={{ fontSize: 13, fontWeight: 700, color: '#111' }}>{igTxt(st.instagram)}</a>}
              </div>
              <button type="button" onClick={() => setSel(null)} aria-label="Tutup" style={{ alignSelf: 'start', background: 'none', border: 0, fontSize: 22, lineHeight: 1, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
              <span className={`tvpp-tag ${stGot ? 'ok' : ''}`}>{stGot ? '✓ Sudah distamp' : 'Belum distamp'}</span>
              {!stGot && onScan && <button type="button" className="tvpp-btn" style={{ padding: '10px 12px', fontSize: 12, boxShadow: '3px 3px 0 #111' }} onClick={onScan}>Scan QR booth ini</button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
